import React from 'react';
import {
  TableBuildHead,
  TableAddRow,
  TableDeleteRows,
  TableWatchShowLogic,
  TableBuildColumnOptions,
  TableBuildRows,
  refreshColumnsOptions,
  TableGetChildren,
} from './TableFactoryHelpers';

/* Table Controls used from ../controls */
import EntityTable from '../controls/EntityTable';
import HorizontalTable from '../controls/HorizontalTable';
import HTComboTable from '../controls/HTComboTable';
import SelectTable from '../controls/SelectTable';

/*
  tablehdr -> tableRow 1:1 relationship

  Table {
    tableHdr: [],
    tableRow: []
  }

  Different types of tables: HTCombo, Horizontal, Entity

*/

function TableFactory(data, parserFn, currentElements, elementActions) {
  if (data.tableHdr === undefined || data.tableHdr.length === 0) {
    console.error(data);
    throw Error('TableFactory: missing or empty `tableHdr` property');
  }

  if (data.tableRow === undefined) {
    throw Error('TableFactory: missing `tableRow` property');
  }

  const srcRows = data.tableRow === null ? [] : data.tableRow;

  /*
   * Bridge functions between Parser and Table model
   */

  let updateModel = () => {};
  let updateColumn = () => {};
  let setTableModel = () => {};
  let rowId = data.maxRowInstanceID;
  let cancelSelectedRows = () => {};
  const rowsPerPage = 5;

  const setUpdateModelFn = fn => (updateModel = fn);
  const getUpdateModelFn = (origRowIndex, colIndex) => newValue => updateModel(origRowIndex, colIndex, true, false, newValue);
  const setRowId = () => ++rowId;

  const setUpdateColumnFn = fn => (updateColumn = fn);
  const getUpdateColumnFn = colIndex => (property, newValue) => updateColumn(colIndex, property, newValue);

  const setTableModelFn = fn => (setTableModel = fn);
  const getSetTableModelFn = () => setTableModel;

  const setCancelSelectedRowsFn = fn => (cancelSelectedRows = fn);
  const getCancelSelectedRowsFn = () => cancelSelectedRows;

  const tableActions = {
    getUpdateModelFn,
    getUpdateColumnFn,
    getSetTableModelFn,
    getRefreshTableFn: refreshTable,
    setRowId,
    getTableProps: () => data,
    onUpdateState: TableWatchShowLogic(getUpdateColumnFn),
  };

  function refreshTable() {
    const { getParserState } = elementActions();
    const { elements, elementsIndex } = getParserState();

    const { tableElements } = TableGetChildren(tableHeadElements, elementActions);

    const elPosition = elementsIndex[data.elementName];
    const elNode = elements[elPosition];

    const oldColumns = TableBuildColumnOptions(tableHeadElements, elementActions, tableActions);
    const newColumns = refreshColumnsOptions(tableElements, oldColumns);
    // refresh the table to reflect the new rows

    elNode.updateComponent({ columns: newColumns });
  }
  const { tableHeadElements, tableHeadOrder } = TableBuildHead(data.elementName, data.tableHdr, parserFn);
  const { rowsElements, rowsValues } = TableBuildRows(srcRows, tableHeadElements, elementActions, tableActions);

  const addNewRowFn = (defaultValues = undefined) => {
    TableAddRow(defaultValues, tableHeadElements, tableHeadOrder, elementActions, tableActions);
  };

  const deleteRowsFn = selectedRows => {
    TableDeleteRows(selectedRows, tableHeadElements, tableHeadOrder, elementActions, tableActions);
    const cancelRowFn = getCancelSelectedRowsFn();
    cancelRowFn();
  };

  let defaultRowsSelected = [];
  Object.entries(srcRows).forEach(([key, row]) => {
    if (row[0].isRowSelected) {
      const rowIdx = parseInt(key, 10) - 1;
      defaultRowsSelected.push({
        rowIndex: rowIdx,
        origRowIndex: rowIdx,
      });
    }
  });

  const columns = TableBuildColumnOptions(tableHeadElements, elementActions, tableActions);

  const tableType = data.controlTypeName;
  let TableComponent = false;

  switch (tableType) {
    case 'Horizontal':
      TableComponent = HorizontalTable;
      break;
    case 'HTCombo':
      TableComponent = HTComboTable;
      break;
    case 'SelectTable':
      TableComponent = SelectTable;
      break;
    case 'Entity':
      TableComponent = EntityTable;
      break;
    default:
      throw Error(`TableFactory: Unknown table type ${data.controlTypeName}`);
  }

  const FinalComponent = props => {
    return (
      <TableComponent
        rows={rowsValues}
        columns={columns}
        selectableRows={true}
        defaultRowsSelected={defaultRowsSelected}
        tableHeadElements={tableHeadElements}
        elementActions={elementActions}
        addNewRowFn={addNewRowFn}
        refreshTableFn={refreshTable}
        deleteRowsFn={deleteRowsFn}
        cancelRowsFn={getCancelSelectedRowsFn}
        setUpdateModelFn={setUpdateModelFn}
        setUpdateColumnFn={setUpdateColumnFn}
        setTableModelFn={setTableModelFn}
        setCancelSelectedRows={setCancelSelectedRowsFn}
        rowsPerPage={rowsPerPage}
        {...props}
      />
    );
  };

  return { elements: rowsElements, component: FinalComponent, onInitialization: () => refreshTable() };
}

export default TableFactory;
